// src/pages/CompareTools.tsx - OPTIMIZED
import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { toolCategories } from '../data/toolsByCategory';
import { Tool } from '../data/types';
import InFeedAd from '../components/Ads/InFeedAd';

const CompareTools: React.FC = () => {
  const [selectedTools, setSelectedTools] = useState<Tool[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showToast, setShowToast] = useState(false);

  const allTools = useMemo(() => {
    return toolCategories.flatMap(category => category.tools);
  }, []);

  const categories = useMemo(() => {
    const allCats = toolCategories.map(cat => cat.title);
    return ['All', ...allCats];
  }, []);

  const filteredTools = useMemo(() => {
    let filtered = allTools;

    if (selectedCategory !== 'All') {
      const category = toolCategories.find(cat => cat.title === selectedCategory);
      if (category) {
        filtered = category.tools;
      }
    }

    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(tool => {
        const titleMatch = tool.title.toLowerCase().includes(searchLower);
        const descMatch = tool.description.toLowerCase().includes(searchLower);
        const tagsMatch = tool.tags && tool.tags.some(tag => 
          tag.toLowerCase().includes(searchLower)
        );
        
        return titleMatch || descMatch || tagsMatch;
      });
    }

    return filtered;
  }, [allTools, searchTerm, selectedCategory]);

  // SEO data - ENHANCED
  const seoData = useMemo(() => {
    const totalTools = allTools.length;
    const totalCategories = categories.length - 1;
    const freeTools = allTools.filter(t => t.isFree).length;
    
    return {
      title: 'Compare AI Tools Side-by-Side - Feature & Pricing Comparison | TooliQ.ai',
      description: `Compare ${totalTools}+ AI tools across ${totalCategories} categories. Analyze features, pricing, and capabilities side-by-side. ${freeTools}+ free options available. Make informed decisions with our comprehensive comparison tool.`,
      keywords: 'AI tools comparison, compare AI software, side-by-side comparison, AI tool features, pricing comparison, best AI tools, tool analysis, feature comparison',
      canonical: 'https://tooliq.ai/compare'
    };
  }, [allTools.length, categories.length]);

  const toggleToolSelection = (tool: Tool) => {
    if (selectedTools.some(t => t.title === tool.title)) {
      setSelectedTools(selectedTools.filter(t => t.title !== tool.title));
    } else if (selectedTools.length < 4) {
      setSelectedTools([...selectedTools, tool]);
    } else {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  const removeTool = (toolTitle: string) => {
    setSelectedTools(selectedTools.filter(t => t.title !== toolTitle));
  };

  const clearSelection = () => setSelectedTools([]);
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('All');
  };

  const comparisonFeatures = [
    { key: 'description', label: 'Description' },
    { key: 'isFree', label: 'Free Tier' },
    { key: 'icon', label: 'Type' },
    { key: 'tags', label: 'Key Features' },
  ];

  const getToolTags = (tool: Tool) => tool.tags || [];

  const getToolCategory = (tool: Tool) => {
    const category = toolCategories.find(cat => 
      cat.tools.some(t => t.title === tool.title)
    );
    return category?.title || 'General';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-100 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />
        
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "AI Tools Comparison Tool",
            "description": seoData.description,
            "url": seoData.canonical,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "permissions": "browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
          <div className="bg-red-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3">
            <span className="text-lg">⚠️</span>
            <div>
              <p className="font-semibold">Maximum tools selected</p>
              <p className="text-sm opacity-90">You can only compare up to 4 tools at a time</p>
            </div>
            <button 
              onClick={() => setShowToast(false)}
              className="text-white hover:text-gray-200 text-lg"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header - ENHANCED */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 dark:text-slate-200 mb-4">
            Compare AI Tools Side-by-Side
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Select up to 4 AI tools to compare features, pricing, and capabilities. 
            Make informed decisions with detailed side-by-side analysis.
          </p>
        </div>

        {/* SEO Content Section */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-2xl p-6 mb-8 border border-slate-200 dark:border-slate-600">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-3">
            Comprehensive AI Tools Comparison Platform
          </h2>
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
            Compare <strong>{allTools.length}+ AI tools</strong> across <strong>{categories.length - 1} categories</strong>. 
            Our comparison tool helps you analyze <strong>features</strong>, <strong>pricing</strong>, and 
            <strong>capabilities</strong> to find the perfect AI solution for your needs. Updated daily with the 
            latest tools and features.
          </p>
        </div>

        {/* Selected Tools Bar */}
        {selectedTools.length > 0 && (
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-slate-200 dark:border-slate-600 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-lg">
                Selected for Comparison ({selectedTools.length}/4)
              </h3>
              <button
                onClick={clearSelection}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
              >
                Clear All
              </button>
            </div>

            <div className="flex flex-wrap gap-3">
              {selectedTools.map(tool => (
                <div
                  key={tool.title}
                  className="flex items-center gap-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl px-4 py-3 border border-purple-200 dark:border-purple-700"
                >
                  <span className="text-xl">{tool.icon}</span>
                  <span className="font-medium text-slate-800 dark:text-slate-200">
                    {tool.title}
                  </span>
                  <button
                    onClick={() => removeTool(tool.title)}
                    className="text-slate-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400 transition-colors"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filter Section */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-slate-200 dark:border-slate-600 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search AI tools by name, description, or features..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  ×
                </button>
              )}
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            {(searchTerm || selectedCategory !== 'All') && (
              <button
                onClick={clearFilters}
                className="px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-gray-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
              >
                Clear Filters
              </button>
            )}
          </div>
          
          <div className="flex justify-between items-center text-sm">
            <div className="text-slate-600 dark:text-slate-400">
              {filteredTools.length} tools available
              {(searchTerm || selectedCategory !== 'All') && (
                <span className="ml-2">
                  {searchTerm && `for "${searchTerm}"`}
                  {searchTerm && selectedCategory !== 'All' && ' in '}
                  {selectedCategory !== 'All' && `${selectedCategory}`}
                </span>
              )}
            </div>
            {selectedTools.length > 0 && (
              <div className="text-slate-600 dark:text-slate-400 font-medium">
                {selectedTools.length} of 4 selected
              </div>
            )}
          </div>
        </div>

        {/* Tools Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          {filteredTools.length > 0 ? (
            filteredTools.map((tool, index) => (
              <React.Fragment key={tool.title}>
                <div
                  onClick={() => toggleToolSelection(tool)}
                  className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 border-2 cursor-pointer transition-all hover:shadow-lg ${
                    selectedTools.some(t => t.title === tool.title)
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : selectedTools.length >= 4
                      ? 'border-slate-200 dark:border-slate-600 opacity-60 cursor-not-allowed'
                      : 'border-slate-200 dark:border-slate-600 hover:border-purple-300 dark:hover:border-purple-700'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-2xl flex-shrink-0">{tool.icon}</span>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-800 dark:text-slate-200 line-clamp-1">
                          {tool.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          {tool.isFree && (
                            <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                              Free
                            </span>
                          )}
                          <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {getToolCategory(tool)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 ${
                      selectedTools.some(t => t.title === tool.title)
                        ? 'bg-purple-500 border-purple-500'
                        : selectedTools.length >= 4
                        ? 'border-slate-300 dark:border-slate-500 opacity-50'
                        : 'border-slate-300 dark:border-slate-500'
                    }`}>
                      {selectedTools.some(t => t.title === tool.title) && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                    {tool.description}
                  </p>
                  {tool.tags && tool.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {tool.tags.slice(0, 2).map(tag => (
                        <span 
                          key={tag} 
                          className="inline-block px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                      {tool.tags.length > 2 && (
                        <span className="inline-block px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full">
                          +{tool.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* SINGLE STRATEGIC AD - After 12th tool */}
                {index === 11 && filteredTools.length > 12 && (
                  <div className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 my-4">
                    <InFeedAd />
                  </div>
                )}
              </React.Fragment>
            ))
          ) : (
            <div className="col-span-full text-center py-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-600">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                No Tools Found
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                No tools found for "{searchTerm}"{selectedCategory !== 'All' && ` in ${selectedCategory}`}
              </p>
              <button
                onClick={clearFilters}
                className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Comparison Table */}
        {selectedTools.length > 0 && (
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-200 dark:border-slate-600 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                Side-by-Side Comparison
              </h2>
              <button
                onClick={clearSelection}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
              >
                Reset Comparison
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-600">
                    <th className="text-left py-4 px-4 font-semibold text-slate-800 dark:text-slate-200 min-w-[200px]">
                      Features
                    </th>
                    {selectedTools.map(tool => (
                      <th key={tool.title} className="text-center py-4 px-4 min-w-[250px]">
                        <div className="flex items-center gap-3 justify-center">
                          <span className="text-2xl">{tool.icon}</span>
                          <div>
                            <div className="font-semibold text-slate-800 dark:text-slate-200">
                              {tool.title}
                            </div>
                            <a 
                              href={tool.toolUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
                            >
                              Visit Website
                            </a>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, index) => (
                    <tr key={feature.key} className={index % 2 === 0 ? 'bg-slate-50/50 dark:bg-gray-700/50' : ''}>
                      <td className="py-4 px-4 font-medium text-slate-700 dark:text-slate-300 border-r border-slate-200 dark:border-slate-600">
                        {feature.label}
                      </td>
                      {selectedTools.map(tool => (
                        <td key={`${tool.title}-${feature.key}`} className="py-4 px-4 text-center border-r border-slate-200 dark:border-slate-600 last:border-r-0">
                          {feature.key === 'isFree' ? (
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              tool.isFree 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {tool.isFree ? 'Yes' : 'No'}
                            </span>
                          ) : feature.key === 'icon' ? (
                            <div className="flex flex-col items-center gap-2">
                              <span className="text-2xl">{tool.icon}</span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {getToolCategory(tool)}
                              </span>
                            </div>
                          ) : feature.key === 'tags' ? (
                            <div className="flex flex-wrap gap-1 justify-center">
                              {getToolTags(tool).slice(0, 4).map(tag => (
                                <span 
                                  key={tag} 
                                  className="inline-block px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                              {getToolTags(tool).length === 0 && (
                                <span className="text-xs text-slate-400 dark:text-slate-500">
                                  No tags
                                </span>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-600 dark:text-slate-400 text-left">
                              {tool[feature.key as keyof Tool] as string}
                            </p>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {selectedTools.length === 0 && filteredTools.length > 0 && (
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-600">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
              Select Tools to Compare
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-2xl mx-auto">
              Choose up to 4 AI tools from the grid above to see a detailed feature comparison. 
              Compare pricing, features, and capabilities side-by-side to make the best choice.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareTools;